import type { ChangeEvent } from "react"
import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Grid } from "@mui/material"
import { Checkbox, Typography } from "@synapse-analytics/synapse-ui"

import type { PermissionAccordionProps, SingleAccordionProps } from "./Interfaces"
import {
  areAllChildrenChecked,
  areSomeChildrenChecked,
  buildChildPermissions,
  generateChildKeys,
  handleChildCheckboxChange,
  handleParentCheckboxChange,
} from "./helpers"

import styles from "./PermissionsAccordion.module.scss"

// Single Accordion Component
const SinglePermissionAccordion: React.FC<SingleAccordionProps> = ({
  parentKey,
  parentObj,
  childPermissions,
  checkPermissions,
  setCheckedPermissions,
  isAdmin,
  isReadMode,
  keyPrefix,
}) => {
  const { t } = useTranslation("settings")

  // Add prefix to child keys for comparison
  const childKeys = generateChildKeys(childPermissions, keyPrefix)
  const allChildrenChecked = areAllChildrenChecked(childKeys, checkPermissions)
  const someChildrenChecked = areSomeChildrenChecked(childKeys, checkPermissions)

  // Handle parent checkbox change
  const handleParentChange = (e: ChangeEvent<HTMLInputElement>): void => {
    handleParentCheckboxChange(e, childKeys, checkPermissions, setCheckedPermissions, isReadMode, isAdmin)
  }

  return (
    <Accordion defaultExpanded className={styles.accordion}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
        aria-controls={`${parentKey}-content`}
        id={`${parentKey}-header`}
        className={styles.accordionSummary}
      >
        <div className={styles.checkboxContainer}>
          <Checkbox
            label={parentObj.label}
            name={`parent-${keyPrefix}-${parentKey}`}
            value={`${keyPrefix}-${parentKey}`}
            checked={isAdmin || allChildrenChecked}
            indeterminate={!isAdmin && someChildrenChecked}
            onChange={handleParentChange}
            readOnly={isReadMode || isAdmin}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails className={styles.accordionDetails}>
        <Grid container spacing={2}>
          {childPermissions.map(({ key, obj }) => {
            const prefixedKey = `${keyPrefix}-${key}`
            return (
              <Grid item xs={6} key={prefixedKey}>
                <Checkbox
                  className={styles.childCheckbox}
                  label={obj.label}
                  name={prefixedKey}
                  value={prefixedKey}
                  checked={isAdmin || checkPermissions.includes(prefixedKey)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleChildCheckboxChange(e, prefixedKey, checkPermissions, setCheckedPermissions)
                  }}
                  readOnly={isReadMode}
                />
              </Grid>
            )
          })}
          {childPermissions.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="p">{t("roleCreationDialog.labels.noPermissionsAvailable")}</Typography>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

// Main Permission Accordion Component
export const PermissionAccordion: React.FC<PermissionAccordionProps> = ({
  permissions,
  checkPermissions,
  setCheckedPermissions,
  isAdmin = false,
  isReadMode = false,
  keyPrefix = "",
}) => {
  return (
    <Fragment>
      {Object.entries(permissions).map(([parentKey, parentObj]) => {
        const childPermissions = buildChildPermissions(parentObj)

        return (
          <SinglePermissionAccordion
            key={`${keyPrefix}-${parentKey}`}
            parentKey={parentKey}
            parentObj={parentObj}
            childPermissions={childPermissions}
            checkPermissions={checkPermissions}
            setCheckedPermissions={setCheckedPermissions}
            isAdmin={isAdmin}
            isReadMode={isReadMode}
            keyPrefix={keyPrefix}
          />
        )
      })}
    </Fragment>
  )
}
